import { useEffect, useRef, useState } from 'react';
import { applyEssenceChange, DAMAGE_CHIP_DRAIN_MS, DAMAGE_CHIP_HOLD_MS, IDLE_DAMAGE_CHIP, type DamageChipState } from './damage-chip';

/**
 * Tracks a bar's damage chip as its value changes (#201): each drop starts
 * or grows the chip and restarts its hold; once the hold is over it drains,
 * then clears. Off, it never starts one.
 */
export function useDamageChip(value: number, enabled: boolean): DamageChipState {
    const [chip, setChip] = useState<DamageChipState>(IDLE_DAMAGE_CHIP);
    const previousValue = useRef(value);

    useEffect(() => {
        const previous = previousValue.current;
        previousValue.current = value;
        if (enabled && previous !== value) {
            setChip((current) => applyEssenceChange(current, previous, value));
        }
    }, [enabled, value]);

    useEffect(() => {
        if (chip.fromValue === null) {
            return;
        }
        const timer = chip.draining
            ? setTimeout(() => setChip((current) => (current.id === chip.id && current.draining ? { ...IDLE_DAMAGE_CHIP, id: current.id } : current)), DAMAGE_CHIP_DRAIN_MS)
            : setTimeout(() => setChip((current) => (current.id === chip.id ? { ...current, draining: true } : current)), DAMAGE_CHIP_HOLD_MS);
        return () => clearTimeout(timer);
        // A new hit (more taken) restarts the hold.
    }, [chip.id, chip.draining, chip.fromValue, chip.taken]);

    return chip;
}
